import React, { useEffect, useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import InfoIcon from "../../AdminMetricDashboard/InfoTextWithIcon";
import { fetchLoginActivity } from '../../../../data/loginActivity';
import LoadingSpinner from "../../AdminMetricDashboard/LoadingIndicator/LoadingSpinner";
import { newUsersByDay } from "../../../../callable-cloud-functions/cloudFunctions";
import { getRangeFromSelection } from "../../../../utils/insighthubDateRange";

const parseDate = (value: string) => new Date(`${value}T00:00:00`);

const formatChartDate = (date: Date) => {
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${month}/${day}`;
};

function SeekerActivityChart({ setActiveandNewUserData = () => null, daysSelected }: any) {
    const [userData, setUserData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [chartTitle, setChartTitle] = useState("Last 14 Days");
    const [loading, setLoading] = useState(true);
   
    // Wrap Legend with a runtime cast to avoid the JSX typing mismatch
    const LegendComponent = (props: any) => {
        return React.createElement(Legend as any, props);
    };

    useEffect(() => {
    async function loadAllData() {
      try {
        setLoading(true);

        // Fetch enough history to support previous full quarter/year filters.
        const [loginHistoricalData, chartResponse]: any = await Promise.all([
          fetchLoginActivity(730),
          newUsersByDay(730),
        ]);

        const chartData = chartResponse?.data || [];

        // Step 1: Count logins per date
        const loginCountByDate = (loginHistoricalData || []).reduce(
          (acc: any, item: any) => {
            acc[item.createdAt] = (acc[item.createdAt] || 0) + 1;
            return acc;
          },
          {}
        );

        // Step 2: Override activeUsers using login activity
        const mergedData = chartData.map((item: any) => ({
          ...item,
          activeUsers: loginCountByDate[item.date] || 0,
        }));
        setUserData(mergedData);
      } catch (err) {
        console.error("Error loading seeker activity data:", err);
      } finally {
        setLoading(false);
      }
    }

    loadAllData();
    // One load: chart + login both cover 365 days; slicing by `daysSelected` is local only.
  }, []);

    useEffect(() => {
        if (userData.length > 0) {
            const { startDate, endDate, title } = getRangeFromSelection(daysSelected);
            const filteredByRange = userData.filter((item: any) => {
                const itemDate = parseDate(item.date);
                return itemDate >= startDate && itemDate <= endDate;
            });
            const mapped = filteredByRange.map((item: any) => ({
                date: formatChartDate(parseDate(item.date)),
                newUsers: item.newUsers,
                activeUsers: item.activeUsers,
            }));
            const totals = mapped.reduce(
                (acc: any, item: any) => {
                    acc.activeUsers += item.activeUsers;
                    acc.newUsers += item.newUsers;
                    return acc;
                },
                { activeUsers: 0, newUsers: 0 }
            );
            setActiveandNewUserData(totals)

            setFilteredData(mapped);
            setChartTitle(title);
        }
    }, [daysSelected, userData]);

    return (
        <div className="bg-white rounded-xl  border border-gray-200 p-6">
            {/* Header */}
            <div className="flex flex-row items-center justify-between mb-4">
                <div>
                    {/* <h2 className="text-xl font-semibold text-[#1E596D] flex items-center gap-2">
                       Seeker Activity Trends
                        <InfoIcon tooltipText="Number of new users who created an account on the MP vs number of users who access the MP in the selected time period." />
                    </h2> */}
                    {/* <p className="text-sm text-[#3b7f96] mt-1">{chartTitle}</p> */}
                    <div>
                        <h2 className="text-2xl text-instant-teams-blue-Main font-bold text-foreground">
                            Seeker Activity Trends
                        </h2>
                        <h2 className="text-lg text-gray-400">New registrations and active seekers</h2>
                    </div>
                </div>
            </div>

            {/* Chart */}
            {loading ? (
                <div className="w-full h-[280px] flex items-center justify-center">
                    <LoadingSpinner />
                </div>
            ) : (
                <div className="w-full h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={filteredData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis
                                dataKey="date"
                                stroke="#6b7280"
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis stroke="#6b7280" />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "0.5rem",
                                }}
                            />

                            <LegendComponent />
                            <Line
                                type="monotone"
                                dataKey="newUsers"
                                stroke="#1E596D"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                                name="New Users"
                            />
                            <Line
                                type="monotone"
                                dataKey="activeUsers"
                                stroke="#3aa4c4"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                                name="Logged-in Users"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

export default SeekerActivityChart;
